import { useState } from 'react';
import { toast } from 'sonner';

import { useAssignRoleMutation } from '@/modules/administration/mutations/useAssignRoleMutation';
import { useAssignTenantMutation } from '@/modules/administration/mutations/useAssignTenantMutation';
import { useRemoveRoleMutation } from '@/modules/administration/mutations/useRemoveRoleMutation';
import { useRemoveTenantMutation } from '@/modules/administration/mutations/useRemoveTenantMutation';
import { useSetDefaultTenantMutation } from '@/modules/administration/mutations/useSetDefaultTenantMutation';
import type { Role } from '@/modules/auth/roles';
import { useCompaniesQuery } from '@/modules/companies/queries/useCompaniesQuery';
import { useSearchKeyword } from '@/modules/search/hooks/useSearchKeyword';
import { useUsersQuery } from '@/modules/users/queries/useUsersQuery';
import type { UserApi } from '@/modules/users/types';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

export const useUserAdministration = () => {
  const { data: users, isLoading: isUsersLoading } = useUsersQuery();
  const { data: companies, isLoading: isCompaniesLoading } =
    useCompaniesQuery();

  const assignTenantMutation = useAssignTenantMutation();
  const removeTenantMutation = useRemoveTenantMutation();
  const setDefaultTenantMutation = useSetDefaultTenantMutation();
  const assignRoleMutation = useAssignRoleMutation();
  const removeRoleMutation = useRemoveRoleMutation();

  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignRoleModalVisible, setAssignRoleModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserApi | null>(null);
  const [manageTenantsUser, setManageTenantsUser] = useState<UserApi | null>(
    null,
  );
  const [manageRolesUser, setManageRolesUser] = useState<UserApi | null>(null);
  const { searchKeyword, onChange, filterItemsBySearchKeyword } =
    useSearchKeyword();

  const handleAssignClick = (user: UserApi) => {
    setSelectedUser(user);
    setAssignModalVisible(true);
  };

  const handleAssignRoleClick = (user: UserApi) => {
    setSelectedUser(user);
    setAssignRoleModalVisible(true);
  };

  const handleAssignSubmit = (userId: string, tenantId: string) => {
    assignTenantMutation.mutate(
      {
        userId,
        tenantId,
      },
      {
        onSuccess: () => {
          toast.success('Tenant assigned successfully');
          setAssignModalVisible(false);
        },
        onError: (error) => {
          toast.error(`Failed to assign tenant: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  const handleAssignRoleSubmit = (userId: string, role: Role) => {
    assignRoleMutation.mutate(
      {
        userId,
        role,
      },
      {
        onSuccess: () => {
          toast.success('Role assigned successfully');
          setAssignRoleModalVisible(false);
        },
        onError: (error) => {
          toast.error(`Failed to assign role: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  const handleRemoveTenant = (userId: string, tenantId: string) => {
    removeTenantMutation.mutate(
      { userId, tenantId },
      {
        onSuccess: () => {
          toast.success('Tenant removed successfully');
        },
        onError: (error) => {
          toast.error(`Failed to remove tenant: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  const handleRemoveRole = (userId: string, role: Role) => {
    removeRoleMutation.mutate(
      { userId, role },
      {
        onSuccess: () => {
          toast.success('Role removed successfully');
        },
        onError: (error) => {
          toast.error(`Failed to remove role: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  const handleSetDefaultTenant = (userId: string, tenantId: string) => {
    setDefaultTenantMutation.mutate(
      { userId, tenantId },
      {
        onSuccess: () => {
          toast.success('Default tenant set successfully');
        },
        onError: (error) => {
          toast.error(
            `Failed to set default tenant: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  // Filter companies that the selected user is NOT already assigned to
  const availableCompanies = companies?.filter(
    (company) => !selectedUser?.tenantIds?.includes(company.id),
  );

  const filteredUsers = users?.filter(
    (user) =>
      filterItemsBySearchKeyword(user, (user) => user.firstName) ||
      filterItemsBySearchKeyword(user, (user) => user.lastName) ||
      filterItemsBySearchKeyword(user, (user) => user.email) ||
      filterItemsBySearchKeyword(user, (user) =>
        `${user.firstName} ${user.lastName}`.trim(),
      ),
  );

  const isLoading = isUsersLoading || isCompaniesLoading;

  return {
    users: filteredUsers,
    companies,
    isLoading,
    searchKeyword,
    onSearchChange: onChange,

    // Modal states
    assignModalVisible,
    setAssignModalVisible,
    assignRoleModalVisible,
    setAssignRoleModalVisible,
    selectedUser,
    manageTenantsUser,
    setManageTenantsUser,
    manageRolesUser,
    setManageRolesUser,
    availableCompanies,

    // Handlers
    handleAssignClick,
    handleAssignRoleClick,
    handleAssignSubmit,
    handleAssignRoleSubmit,
    handleRemoveTenant,
    handleRemoveRole,
    handleSetDefaultTenant,

    // Loading states for mutations
    isAssignTenantPending: assignTenantMutation.isPending,
    isAssignRolePending: assignRoleMutation.isPending,
  };
};
